import { Splide, SplideSlide } from "@splidejs/react-splide";
import React, { useState } from "react";
import { useEffect } from "react";
import "@splidejs/react-splide/css";
import FadeInSection, { FadeInType } from "../wrappers/FadeInSection";

const RotatingText = ({ phrases, interval = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true); // Controls fade-in and fade-out

  useEffect(() => {
    const timer = setInterval(() => {
      setFade(false); // Start fade-out

      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % phrases.length);
        setFade(true); // Start fade-in after text changes
      }, 500); // Match fade-out duration
    }, interval);

    return () => clearInterval(timer); // Cleanup interval on component unmount
  }, [phrases, interval]);

  return (
    <span className="rotator-fade text-primary">
      <span
        className={`text-[22px] sm:text-[34px] md:text-[46px] transition-opacity duration-500 ${
          fade ? "opacity-100" : "opacity-0"
        }`}
        style={{
          display: "inline-block",
        }}
      >
        {phrases[currentIndex]}
      </span>
    </span>
  );
};

const imagePaths = [
  {
    path: "/assets/home/hero/kids_at_legobins (1).jpg",
    desc: "Kids at Legobins",
  },
  {
    path: "/assets/home/hero/IMG_6790.jpg",
    desc: "Tower Challenge",
  },
  {
    path: "/assets/home/hero/IMG_6795.jpg",
    desc: "Tower Example",
  },
  {
    path: "/assets/home/hero/IMG_6802.jpg",
    desc: "Tower Challenge",
  },
  {
    path: "/assets/home/hero/hackathon-2.jpg",
    desc: "Coding Challenge",
  },
  {
    path: "/assets/home/hero/hackathon-1.jpg",
    desc: "Hackathon Winner",
  },
  {
    path: "/assets/home/hero/IMG_6876.jpg",
    desc: "Slime Challenge",
  },
  {
    path: "/assets/home/hero/image_5.jpeg",
    desc: "FSC Fair",
  },
  {
    path: "/assets/home/hero/IMG_0561.jpeg",
    desc: "STEM Labs",
  },
];

export default function Hero() {
  return (
    <section
      id="home"
      className="relative z-40 overflow-hidden pt-28 pb-24 sm:pt-36 lg:pt-[170px] lg:pb-[120px] min-h-screen flex"
    >
      <div className="px-4 xl:mx-auto flex">
        <div className="-mx-4 flex flex-wrap items-center justify-center flex-col md:flex-row">
          <div className="w-5/6 md:w-1/2 px-3 justify-center flex">
            <FadeInSection distance={FadeInType.SHORT}>
              <div className="mb-12 max-w-[600px] text-center lg:mb-0 lg:ml-0 lg:text-left">
                <h1 className="font-main mb-5 font-semibold text-2xl sm:text-4xl md:text-[50px] md:leading-[60px] dark:text-white">
                  STEM Enrichment of <br />
                  New Jersey <br className="md:hidden" />
                  <RotatingText
                    phrases={[
                      "Spreading STEM Literacy",
                      "Empowering Youth",
                      "Innovating Education",
                      "Inspiring Future Leaders",
                    ]}
                  />
                </h1>
                <p className="text-dark-text mb-12 text-base">
                  Proudly registered 501(c)(3) non-profit and certified as a
                  Presidential Volunteer Service Award organization.
                </p>
                <div className="flex flex-wrap items-center justify-center lg:justify-start">
                  <a
                    href="#about"
                    className="bg-primary font-main hover:bg-primary/90 inline-flex items-center rounded-sm px-6 py-[10px] text-base text-white md:px-8 md:py-[14px]"
                  >
                    Learn More
                    <span className="pl-3">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.172 7L6.808 1.636L8.222 0.222L16 8L8.222 15.778L6.808 14.364L12.172 9H0V7H12.172Z"
                          fill="white"
                        ></path>
                      </svg>
                    </span>
                  </a>
                  <a
                    href="#join"
                    className="font-main text-dark hover:text-primary dark:hover:text-primary inline-flex items-center rounded-sm px-8 py-[14px] text-base dark:text-white"
                  >
                    <span className="pr-3">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        className="fill-current"
                      >
                        <path d="M19.376 12.416L8.777 19.482C8.70171 19.5321 8.61423 19.5608 8.52389 19.5652C8.43355 19.5695 8.34373 19.5492 8.264 19.5065C8.18427 19.4639 8.1176 19.4003 8.07111 19.3228C8.02462 19.2452 8.00005 19.1564 8 19.066V4.934C8.00005 4.84356 8.02462 4.75482 8.07111 4.67724C8.1176 4.59966 8.18427 4.53615 8.264 4.49346C8.34373 4.45077 8.43355 4.43051 8.52389 4.43483C8.61423 4.43915 8.70171 4.46789 8.777 4.518L19.376 11.584C19.4445 11.6297 19.5006 11.6915 19.5395 11.7641C19.5783 11.8367 19.5986 11.9177 19.5986 12C19.5986 12.0823 19.5783 12.1633 19.5395 12.2359C19.5006 12.3085 19.4445 12.3703 19.376 12.416Z"></path>
                      </svg>
                    </span>
                    Start your own chapter
                  </a>
                </div>
              </div>
            </FadeInSection>
          </div>
          <div className="w-5/6 md:w-1/2 px-4 justify-center flex">
            <FadeInSection distance={FadeInType.TOLEFT}>
              <Splide
                options={{
                  type: "loop",
                  perPage: 1,
                  autoplay: true,
                  interval: 3000,
                  pauseOnHover: true,
                  arrows: true,
                  pagination: false,
                  speed: 800,
                  fixedHeight: "75vh",
                }}
                className="max-w-3xl mx-auto"
              >
                {imagePaths.map((slide, index) => (
                  <SplideSlide key={index}>
                    <div className="relative w-full h-full">
                      {/* Set height explicitly */}
                      <img
                        className="w-full h-full object-cover rounded-lg shadow-md" // Enforce consistent sizing
                        src={slide.path}
                        alt={slide.desc}
                      />
                      <div className="absolute top-0 left-0 w-full h-full text-black text-lg flex items-end justify-center hover:opacity-100 transition-opacity duration-300 ease-in-out opacity-0">
                        <div className="w-full py-6 bg-gradient-to-b from-transparent to-black rounded-b-lg">
                          <p className="text-center font-semibold text-white">
                            {slide.desc}
                          </p>
                        </div>
                      </div>
                    </div>
                  </SplideSlide>
                ))}
              </Splide>
            </FadeInSection>
          </div>
        </div>
      </div>
      <div className="absolute top-0 right-0 -z-10">
        <svg
          width="1200"
          height="900"
          viewBox="0 0 1100 900"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g opacity="0.5" filter="url(#filter1)">
            <rect
              x="440.02"
              y="-123.65"
              width="329.62"
              height="957"
              transform="rotate(-40.44 463.78 -125.739)"
              fill="url(#grad1)"
            ></rect>
          </g>
          <defs>
            <filter
              id="filter1"
              x="0.0878906"
              y="-776.711"
              width="1726.24"
              height="1876.4"
              filterUnits="userSpaceOnUse"
            >
              <feFlood floodOpacity="0" result="BackgroundImage"></feFlood>
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="BackgroundImage"
                result="shape"
              ></feBlend>
              <feGaussianBlur stdDeviation="225"></feGaussianBlur>
            </filter>
            <linearGradient
              id="grad1"
              x1="420.6"
              y1="60.5"
              x2="960.732"
              y2="623.2"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#ABBCFF"></stop>
              <stop offset="0.912" stopColor="#4A6CF7"></stop>
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div className="absolute bottom-0 left-0 -z-10">
        <svg
          width="1460"
          height="500"
          viewBox="0 0 1460 500"
          fill="none"
          className="overflow-visible"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g opacity="0.3" filter="url(#filter2)">
            <rect y="460" width="1020" height="250" fill="url(#grad2)"></rect>
          </g>
          <defs>
            <filter
              id="filter2"
              x="-440"
              y="10"
              width="2000"
              height="1050"
              filterUnits="userSpaceOnUse"
            >
              <feFlood floodOpacity="0" result="BackgroundImage"></feFlood>
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="BackgroundImage"
                result="shape"
              ></feBlend>
              <feGaussianBlur stdDeviation="225"></feGaussianBlur>
            </filter>
            <linearGradient
              id="grad2"
              x1="-94.8"
              y1="510.634"
              x2="-63.3028465"
              y2="810.234"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#ABBCFF"></stop>
              <stop offset="0.8453" stopColor="#4A6CF7"></stop>
            </linearGradient>
          </defs>
        </svg>
      </div>
    </section>
  );
}
